import { useTranslation } from 'react-i18next';
import type { OrganisationInvite } from '@ghostscreen/contracts';
import { QueryEmptyState } from '../../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';
import { formatInviteAge, formatInviteRole } from './teamFormat';

type TeamsPendingInvitesCardProps = {
  invites: OrganisationInvite[] | undefined;
  isPending: boolean;
  isError: boolean;
  isRetrying: boolean;
  onRetry: () => void;
  canManage: boolean;
  busyInviteId: string | null;
  onResend: (inviteId: string) => void;
  onRevoke: (inviteId: string) => void;
};

export function TeamsPendingInvitesCard({
  invites,
  isPending,
  isError,
  isRetrying,
  onRetry,
  canManage,
  busyInviteId,
  onResend,
  onRevoke,
}: TeamsPendingInvitesCardProps) {
  const { t } = useTranslation('teams');
  const count = invites?.length ?? 0;

  return (
    <section
      className="rounded-[14px] border border-line px-5 py-5"
      style={{ background: 'var(--gs-panel)' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 text-base font-bold text-ink">{t('pendingInvites.title')}</h2>
        {count > 0 ? (
          <span
            className="rounded-full px-2.5 py-1 text-[11px] font-semibold text-[var(--gs-accent)]"
            style={{
              background: 'color-mix(in srgb, var(--gs-accent) 14%, transparent)',
            }}
          >
            {t('pendingInvites.waiting', { count })}
          </span>
        ) : null}
      </div>

      <div className="mt-4">
        {isError || isRetrying ? (
          <QueryErrorState
            message={t('pendingInvites.loadError')}
            onRetry={onRetry}
            isRetrying={isRetrying}
          />
        ) : isPending ? (
          <QueryLoadingBlock />
        ) : count === 0 ? (
          <QueryEmptyState>{t('pendingInvites.empty')}</QueryEmptyState>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {invites?.map((invite) => {
              const busy = busyInviteId === invite.id;
              return (
                <li
                  key={invite.id}
                  className="flex flex-wrap items-center gap-3 rounded-[12px] border border-line px-3 py-2.5"
                  style={{ background: 'var(--gs-panel2)' }}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">
                      {invite.email}
                    </span>
                    <span className="block text-xs text-muted">
                      {formatInviteAge(invite.updatedAt)} · {formatInviteRole(invite.role)}
                    </span>
                  </span>
                  <span className="flex shrink-0 gap-2">
                    {canManage ? (
                      <>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => onResend(invite.id)}
                          className="rounded-[8px] border border-line px-2.5 py-1.5 text-xs font-semibold text-muted disabled:opacity-50"
                        >
                          {t('pendingInvites.resend')}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => onRevoke(invite.id)}
                          className="rounded-[8px] px-2.5 py-1.5 text-xs font-semibold text-[var(--gs-bad)] disabled:opacity-50"
                        >
                          {t('pendingInvites.revoke')}
                        </button>
                      </>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
